import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { Router, RouterLink } from '@angular/router';
import { ICompany } from '../../core/models/ICompany';
import { IUser } from '../../core/models/IUser';
import { UserService } from '../../core/services/user.service';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { CompanyAdd } from '../../shared/components/company/company-add/company-add';
import { CompanyService } from '../../core/services/company.service';
import { NotificationService } from '../../shared/services/notification.service';
import { ViewportScroller } from '@angular/common';
import { LogService } from '../../core/services/log.service';

Chart.register(...registerables);

interface ConsultationCount {
  date: string;
  count: number;
}

type ChartPeriod = 'today' | '7days' | '30days' |'3months' | '1year';

@Component({
  selector: 'app-dashboard',
  imports: [
    CommonModule,
    CompanyAdd,
    RouterLink
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss'
})
export class Dashboard implements OnInit, AfterViewInit, OnDestroy {
  user: IUser | null = null;
  addCompanyFormVisible = false;
  apiUrl = environment.apiUrl;
  viewsChart: Chart | null = null;

  // Nouvelles propriétés pour la gestion des charts
  selectedPeriod: ChartPeriod = '30days';
  selectedCompanyId: string | null = null;
  companyCharts: Map<string, Chart> = new Map();

  constructor(
    private router: Router,
    private userService: UserService,
    private companyService: CompanyService,
    private notificationService: NotificationService,
    private viewportScroller: ViewportScroller,
    private logger: LogService
  ) { }

  ngOnInit(): void {
    this.loadUserData();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initializeChart();
      this.initializeMiniCharts();
    }, 100);
  }

  loadUserData(): void {
    this.userService.loadProfile().subscribe(user => {
      this.user = user;
      setTimeout(() => {
        this.updateChart();
        this.initializeMiniCharts();
      }, 100);
    });
  }

  initializeChart(): void {
    const canvas = document.getElementById('viewsChart') as HTMLCanvasElement;
    if (!canvas) {
      this.logger.error('Canvas element not found');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.logger.error('Could not get 2D context');
      return;
    }

    if (this.viewsChart) {
      this.viewsChart.destroy();
    }

    const chartData = this.getChartData();

    const config: ChartConfiguration = {
      type: 'line',
      data: {
        labels: chartData.labels,
        datasets: chartData.datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: chartData.datasets.length > 1,
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 15,
              font: {
                size: 12,
                weight: 'bold'
              }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
              size: 14,
              weight: 'bold'
            },
            bodyFont: {
              size: 13
            },
            cornerRadius: 8,
            displayColors: true
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              color: '#6b7280',
              font: {
                size: 12
              }
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(0, 0, 0, 0.05)'
            },
            ticks: {
              color: '#6b7280',
              font: {
                size: 12
              },
              callback: function (value) {
                return value.toLocaleString();
              }
            }
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        }
      }
    };

    this.viewsChart = new Chart(ctx, config);
  }

  getChartData(): { labels: string[], datasets: any[] } {
    const filteredData = this.getFilteredConsultationData();

    if (this.selectedCompanyId) {
      const company = this.user?.companies?.find(c => c.data._id === this.selectedCompanyId);
      if (!company) {
        return { labels: [], datasets: [] };
      }

      return {
        labels: filteredData.map(d => this.formatDateLabel(d.date)),
        datasets: [{
          label: company.data.companyName,
          data: this.getCompanyDataForPeriod(company.data, filteredData.map(d => d.date)),
          borderColor: '#667eea',
          backgroundColor: 'rgba(102, 126, 234, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: '#667eea',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointHoverRadius: 6,
        }]
      };
    } else {
      if (this.user?.companies && this.user.companies.length > 1) {
        const colors = [
          { border: '#667eea', bg: 'rgba(102, 126, 234, 0.1)' },
          { border: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
          { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
          { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
          { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' }
        ];

        const datasets = this.user.companies.map((company, index) => {
          const color = colors[index % colors.length];
          return {
            label: company.data.companyName,
            data: this.getCompanyDataForPeriod(company.data, filteredData.map(d => d.date)),
            borderColor: color.border,
            backgroundColor: color.bg,
            fill: true,
            tension: 0.4,
            pointRadius: 3,
            pointBackgroundColor: color.border,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointHoverRadius: 5,
          };
        });

        return {
          labels: filteredData.map(d => this.formatDateLabel(d.date)),
          datasets: datasets
        };
      } else {
        return {
          labels: filteredData.map(d => this.formatDateLabel(d.date)),
          datasets: [{
            label: 'Vues',
            data: filteredData.map(d => d.count),
            borderColor: '#667eea',
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#667eea',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointHoverRadius: 6,
          }]
        };
      }
    }
  }

  getCompanyDataForPeriod(company: ICompany, dates: string[]): number[] {
    return dates.map(date => {
      const consultation = company.consultationCount?.find(c => c.date === date);
      return consultation ? consultation.count : 0;
    });
  }

  getFilteredConsultationData(): ConsultationCount[] {
    const aggregated = new Map<string, number>();
    const today = new Date();
    let startDate = new Date();

    switch (this.selectedPeriod) {
      case 'today':
        startDate = new Date(today);
        break;
      case '7days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 6);
        break;
      case '30days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case '3months':
        startDate = new Date(today);
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case '1year':
        startDate = new Date(today);
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
    }

    startDate.setHours(0, 0, 0, 0);
    today.setHours(23, 59, 59, 999);

    this.user?.companies?.forEach(company => {
      company.data.consultationCount?.forEach(consultation => {
        const consultDate = new Date(consultation.date);
        consultDate.setHours(0, 0, 0, 0);

        if (consultDate >= startDate && consultDate <= today) {
          const currentCount = aggregated.get(consultation.date) || 0;
          aggregated.set(consultation.date, currentCount + consultation.count);
        }
      });
    });

    return Array.from(aggregated.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  formatDateLabel(dateString: string): string {
    const date = new Date(dateString);

    if (this.selectedPeriod === 'today') {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (this.selectedPeriod === '7days') {
      return date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' });
    } else if(this.selectedPeriod === '30days') {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    } else if (this.selectedPeriod === '3months') {
      return date.toLocaleDateString('fr-FR', { month: 'short', day: '2-digit' });
    } else if (this.selectedPeriod === '1year') {
      return date.toLocaleDateString('fr-FR');
    } else {
      return date.toLocaleDateString('fr-FR');
    }
  }

  changePeriod(period: ChartPeriod): void {
    this.selectedPeriod = period;
    this.updateChart();
  }

  selectCompany(companyId: string | null): void {
    if (this.selectedCompanyId === companyId) {
      this.selectedCompanyId = null;
    } else {
      this.selectedCompanyId = companyId;
    }

    const selector = document.querySelector('.company-selector') as HTMLSelectElement;
    if (selector) {
      selector.value = this.selectedCompanyId || '';
    }

    this.updateChart();
  }

  updateChart(): void {
    if (!this.viewsChart) {
      this.initializeChart();
      return;
    }

    const chartData = this.getChartData();
    this.viewsChart.data.labels = chartData.labels;
    this.viewsChart.data.datasets = chartData.datasets;
    this.viewsChart.update('active');
  }

  initializeMiniCharts(): void {
    this.companyCharts.forEach(chart => chart.destroy());
    this.companyCharts.clear();

    this.user?.companies?.forEach(company => {
      const canvasId = `miniChart-${company.data._id}`;
      const canvas = document.getElementById(canvasId) as HTMLCanvasElement;

      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return;
      }

      const last7Days = this.getLast7DaysData(company.data);

      const chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: last7Days.map(d => {
            const date = new Date(d.date);
            return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
          }),
          datasets: [{
            data: last7Days.map(d => d.count),
            borderColor: '#667eea',
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 4,
            borderWidth: 2,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              enabled: true,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 8,
              cornerRadius: 6,
              displayColors: false,
              callbacks: {
                title: () => '',
                label: (context) => `${context.parsed.y} vues`
              }
            }
          },
          scales: {
            x: {
              display: false
            },
            y: {
              display: false,
              beginAtZero: true
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        }
      });

      this.companyCharts.set(company.data._id, chart);
    });
  }

  getLast7DaysData(company: ICompany): ConsultationCount[] {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);

    sevenDaysAgo.setHours(0, 0, 0, 0);
    today.setHours(23, 59, 59, 999);

    const filtered = company.consultationCount?.filter(consultation => {
      const consultDate = new Date(consultation.date);
      consultDate.setHours(0, 0, 0, 0);
      return consultDate >= sevenDaysAgo && consultDate <= today;
    }) || [];

    const dateMap = new Map<string, number>();

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - (6 - i));
      const dateStr = date.toISOString().split('T')[0];
      dateMap.set(dateStr, 0);
    }

    filtered.forEach(consultation => {
      dateMap.set(consultation.date, consultation.count);
    });

    return Array.from(dateMap.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  aggregateConsultationData(): ConsultationCount[] {
    return this.getFilteredConsultationData();
  }

  getTotalViews(): number {
    let total = 0;
    this.user?.companies?.forEach(company => {
      company.data.consultationCount?.forEach(consultation => {
        total += consultation.count;
      });
    });
    return total;
  }

  getAverageRating(): number {
    if (!this.user?.companies || this.user.companies.length === 0) {
      return 0;
    }

    const visibleCompanies = this.user.companies.filter(company => company.data.isVisible);
    if (visibleCompanies.length === 0) return 0;

    const sum = visibleCompanies.reduce((acc, company) => {
      return acc + (company.data.averageRating || 0);
    }, 0);

    return parseFloat((sum / visibleCompanies.length).toFixed(1));
  }

  getVisibleCompanies(): number {
    return this.user?.companies?.filter(c => c.data.isVisible).length || 0;
  }

  getHiddenCompanies(): number {
    return this.user?.companies?.filter(c => !c.data.isVisible).length || 0;
  }

  getVisibilityPercentage(): number {
    const total = this.user?.companies?.length || 0;
    if (total === 0) return 0;
    return Math.round((this.getVisibleCompanies() / total) * 100);
  }

  addCompany(): void {
    if (this.user?.userPlan?.planName === 'free') {
      this.notificationService.error('Vous devez passer à un plan payant pour créer une société.');
      return;
    }
    if (this.user?.userPlan?.planName === 'basic' && this.user.companies.length >= 1) {
      if (confirm("Vous n'avez pas le droit de créer plus de 1 société avec le plan basic.\n Voulez-vous passer au plan premium pour créer plus de sociétés ?")) {
        this.router.navigate(['/profile']);
      }
      return;
    }
    if (this.user?.userPlan?.planName === 'premium' && this.user.companies.length >= 5) {
      this.notificationService.error('Vous avez atteint le nombre maximum de sociétés autorisées (5).');
      return;
    }
    this.addCompanyFormVisible = true;
    this.viewportScroller.scrollToAnchor('company-form');
  }

  onCompanyAdded(company: ICompany): void {
    this.loadUserData();
    this.addCompanyFormVisible = false;
    this.notificationService.success('Société ajoutée avec succès');
  }

  editCompany(company: ICompany): void {
    this.router.navigate(['/company/edit', company._id]);
  }

  deleteCompany(companyId: string): void {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette société ?\nCette action est irréversible.')) {
      const chart = this.companyCharts.get(companyId);
      if (chart) {
        chart.destroy();
        this.companyCharts.delete(companyId);
      }

      this.companyService.deleteCompany(companyId).subscribe(() => {
        this.notificationService.success('Société supprimée avec succès');
        this.loadUserData();
        this.updateChart();
      });
    }
  }

  toggleVisibility(company: ICompany): void {
    company.isVisible = !company.isVisible;

    this.companyService.editCompany(company._id, { isVisible: company.isVisible })
      .subscribe(() => {
        this.notificationService.success('Visibilité mise à jour');
      });
  }

  refreshData(): void {
    this.notificationService.success('Données rafraîchies avec succès');
    this.loadUserData();
    this.updateChart();

    setTimeout(() => {
      this.initializeMiniCharts();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.viewsChart) {
      this.viewsChart.destroy();
    }

    this.companyCharts.forEach(chart => chart.destroy());
    this.companyCharts.clear();
  }
}